import AppBar from "@mui/material/AppBar";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { useThirdPartyContractDetails } from "./useThirdPartyContractDetails";

function formatMaybeDate(value: string | null | undefined) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function ThirdPartyContractDetailsDialog(props: {
  open: boolean;
  contractId: string | null;
  onClose: () => void;
}) {
  const { open, contractId, onClose } = props;

  const detailsQuery = useThirdPartyContractDetails(contractId, open);
  const contract = detailsQuery.data ?? null;

  const displayName = contract
    ? contract.contractName?.trim() ||
      contract.protocolName?.trim() ||
      "Third party contract"
    : "";

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Toolbar sx={{ gap: 1 }}>
          <Typography variant="h6" sx={{ flex: 1 }}>
            Third party contract details
          </Typography>
          <IconButton aria-label="close" edge="end" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        {detailsQuery.isPending && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {detailsQuery.isError && (
          <Stack spacing={2} sx={{ maxWidth: 960, mx: "auto" }}>
            <Alert
              severity="error"
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => void detailsQuery.refetch()}
                >
                  Retry
                </Button>
              }
            >
              Unable to load contract details.
            </Alert>
          </Stack>
        )}

        {contract && !detailsQuery.isPending && !detailsQuery.isError && (
          <Stack spacing={2.5} sx={{ maxWidth: 1100, mx: "auto" }}>
            <Stack spacing={0.5}>
              <Typography variant="h5" sx={{ wordBreak: "break-word" }}>
                {displayName}
              </Typography>
              {contract.contractName?.trim() && contract.protocolName?.trim() ? (
                <Typography variant="body2" color="text.secondary">
                  {contract.protocolName}
                </Typography>
              ) : null}
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ wordBreak: "break-all" }}
              >
                {contract.id}
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Network
                </Typography>
                <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                  {contract.network}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Network type
                </Typography>
                <Typography variant="body2">{contract.networkType}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Address
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
                >
                  {contract.address}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Version
                </Typography>
                <Typography variant="body2">{contract.version}</Typography>
              </Grid>
              {contract.contractName ? (
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Contract name
                  </Typography>
                  <Typography variant="body2" sx={{ wordBreak: "break-word" }}>
                    {contract.contractName}
                  </Typography>
                </Grid>
              ) : null}
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Description
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                  {contract.description || "—"}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Created
                </Typography>
                <Typography variant="body2">{formatMaybeDate(contract.createdAt)}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Updated
                </Typography>
                <Typography variant="body2">{formatMaybeDate(contract.updatedAt)}</Typography>
              </Grid>
            </Grid>
          </Stack>
        )}
      </Box>
    </Dialog>
  );
}
